import React, { useState, useEffect, useRef } from 'react';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const COGSToolbar = ({
  searchQuery,
  setSearchQuery,
  brands,
  niches,
  selectedBrands,
  setSelectedBrands,
  selectedNiches,
  setSelectedNiches,
  hourlyRate,
  setHourlyRate,
  toolsCost,
  setToolsCost,
  lostPct,
  setLostPct,
  onExportExcel,
  onOpenBizExp,
  bizExpData,
}) => {
  const [brandOpen, setBrandOpen] = useState(false);
  const [nicheOpen, setNicheOpen] = useState(false);
  const brandRef = useRef(null);
  const nicheRef = useRef(null);

  useEffect(() => {
    const handleOut = (e) => {
      if (brandRef.current && !brandRef.current.contains(e.target)) setBrandOpen(false);
      if (nicheRef.current && !nicheRef.current.contains(e.target)) setNicheOpen(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const toggleBrand = (id) => {
    setSelectedBrands(prev =>
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
    );
  };

  const toggleNiche = (id) => {
    setSelectedNiches(prev =>
      prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]
    );
  };

  const fmtUSD  = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtUnit = (v) => { const n = Number(v); return n === 0 ? '$0.00' : `$${parseFloat(n.toFixed(4))}`; };

  return (
    <div className="cogs-toolbar-outer">
    <div className="cogs-toolbar">
      {/* Search */}
      <div className="cogs-search-wrapper">
        <i className="fas fa-search cogs-search-icon"></i>
        <input
          type="text"
          className="cogs-search-input"
          placeholder="Search by product name or SKU..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="cogs-search-clear" onClick={() => setSearchQuery('')}>
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Brand filter */}
      <div className="cogs-filter-wrapper" ref={brandRef}>
        <button
          className={`cogs-filter-btn${selectedBrands.length > 0 ? ' active' : ''}`}
          onClick={() => { setBrandOpen(o => !o); setNicheOpen(false); }}
        >
          <i className="fas fa-tag"></i>
          Brand
          {selectedBrands.length > 0 && (
            <span className="cogs-filter-badge">{selectedBrands.length}</span>
          )}
          <i className={`fas fa-chevron-${brandOpen ? 'up' : 'down'} cogs-chevron`}></i>
        </button>
        {brandOpen && (
          <div className="cogs-dropdown">
            <div className="cogs-dropdown-header">Filter by Brand</div>
            {brands.length === 0 ? (
              <div className="cogs-dropdown-empty">No brands</div>
            ) : brands.map(b => (
              <label key={b.brandID} className="cogs-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(String(b.brandID))}
                  onChange={() => toggleBrand(String(b.brandID))}
                />
                {b.brandName || b.brandID}
              </label>
            ))}
            {selectedBrands.length > 0 && (
              <button className="cogs-dropdown-clear" onClick={() => setSelectedBrands([])}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Niche filter */}
      <div className="cogs-filter-wrapper" ref={nicheRef}>
        <button
          className={`cogs-filter-btn${selectedNiches.length > 0 ? ' active' : ''}`}
          onClick={() => { setNicheOpen(o => !o); setBrandOpen(false); }}
        >
          <i className="fas fa-layer-group"></i>
          Niche
          {selectedNiches.length > 0 && (
            <span className="cogs-filter-badge">{selectedNiches.length}</span>
          )}
          <i className={`fas fa-chevron-${nicheOpen ? 'up' : 'down'} cogs-chevron`}></i>
        </button>
        {nicheOpen && (
          <div className="cogs-dropdown">
            <div className="cogs-dropdown-header">Filter by Niche</div>
            {niches.length === 0 ? (
              <div className="cogs-dropdown-empty">No niches</div>
            ) : niches.map(n => (
              <label key={n.niche_category_id} className="cogs-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedNiches.includes(String(n.niche_category_id))}
                  onChange={() => toggleNiche(String(n.niche_category_id))}
                />
                {n.niche_name}
              </label>
            ))}
            {selectedNiches.length > 0 && (
              <button className="cogs-dropdown-clear" onClick={() => setSelectedNiches([])}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hourly rate */}
      <div className="cogs-hourly-wrapper">
        <label className="cogs-hourly-label">
          <i className="fas fa-clock"></i>
          Labor rate ($/hr)
        </label>
        <input
          type="number"
          className="cogs-hourly-input"
          min="0"
          step="0.5"
          value={hourlyRate}
          onChange={e => setHourlyRate(parseFloat(e.target.value) || 0)}
        />
      </div>

      {/* Tools cost */}
      <div className="cogs-hourly-wrapper">
        <label className="cogs-hourly-label">
          <i className="fas fa-tools"></i>
          Tools ($/set)
        </label>
        <input
          type="number"
          className="cogs-hourly-input"
          min="0"
          step="0.01"
          value={toolsCost}
          onChange={e => setToolsCost(parseFloat(e.target.value) || 0)}
        />
      </div>

      {/* Lost % */}
      <div className="cogs-hourly-wrapper">
        <label className="cogs-hourly-label">
          <i className="fas fa-exclamation-triangle"></i>
          Lost (%)
        </label>
        <input
          type="number"
          className="cogs-hourly-input"
          min="0"
          step="1"
          value={lostPct}
          onChange={e => setLostPct(parseFloat(e.target.value) || 0)}
        />
      </div>

      {/* Business Expenses button */}
      <button className="cogs-bizexp-btn" onClick={onOpenBizExp}>
        <i className="fas fa-building"></i>
        Business Expenses
      </button>

      {/* Export button */}
      <button className="cogs-export-btn" onClick={onExportExcel}>
        <i className="fas fa-file-excel"></i>
        Export Excel
      </button>
    </div>

    {/* Summary bar — shows under toolbar when expenses exist */}
    {bizExpData && (bizExpData.totalExpenses > 0 || bizExpData.totalQty > 0) && (
      <div className="cogs-bizexp-bar">
        <i className="fas fa-building"></i>
        <span className="cogs-bizexp-bar-label">Business Expenses:</span>
        <span className="cogs-bizexp-bar-item">
          <span className="cogs-bizexp-bar-key">Total</span>
          <strong>{fmtUSD(bizExpData.totalExpenses)}</strong>
        </span>
        <span className="cogs-bizexp-bar-sep">·</span>
        <span className="cogs-bizexp-bar-item">
          <span className="cogs-bizexp-bar-key">Units ({MONTHS_SHORT[bizExpData.month - 1]} {bizExpData.year})</span>
          <strong>{Number(bizExpData.totalQty).toLocaleString()}</strong>
        </span>
        <span className="cogs-bizexp-bar-sep">·</span>
        <span className="cogs-bizexp-bar-item">
          <span className="cogs-bizexp-bar-key">Per unit</span>
          <strong className="cogs-bizexp-bar-perunit">{fmtUnit(bizExpData.costPerUnit)}</strong>
        </span>
      </div>
    )}
    </div>
  );
};

export default COGSToolbar;
